/**
 * The bridge between a FieldDef and the design system: one control per
 * `ControlKind`, so no page ever branches on a document type to draw a field.
 */
import { useState } from 'react'
import type { FieldDef } from '@project/contracts'
import { Button, Field, Input, Lookup, Select } from '../design/index.js'
import { asText } from './format.js'
import { QuickMasterDialog } from './QuickMaster.js'

/**
 * Masters somebody adds mid-document (docs/02 §4c, docs/06 §Form).
 *
 * A site address or a supplier's contact is discovered while raising the order
 * that needs it. Sending the buyer to Masters, and back, and asking them to
 * remember where they were, is how a purchase order gets raised against the
 * wrong address instead.
 */
const CREATABLE: Readonly<Record<string, string>> = {
  address: 'address',
  contact: 'contact',
}

export interface ControlProps {
  readonly field: FieldDef
  readonly value: unknown
  readonly onChange: (value: unknown) => void
  readonly disabled?: boolean
  readonly invalid?: boolean
  /** Display text for an id the form already holds, e.g. the supplier's name. */
  readonly valueLabel?: string | undefined
}

/** Money, quantity, rate and percentage inputs align right with tabular figures. */
const DECIMAL_CONTROLS = new Set(['number', 'money', 'qty', 'rate', 'percent'])

export const Control = ({ field, value, onChange, disabled, invalid, valueLabel }: ControlProps) => {
  if (field.control === 'lookup') {
    return (
      <LookupControl
        field={field}
        value={value}
        onChange={onChange}
        {...(disabled === undefined ? {} : { disabled })}
        {...(invalid === undefined ? {} : { invalid })}
        {...(valueLabel === undefined ? {} : { valueLabel })}
      />
    )
  }

  if (field.control === 'select') {
    return (
      <Select
        value={asText(value)}
        disabled={disabled}
        invalid={invalid}
        placeholder="Not chosen"
        options={(field.options ?? []).map((o) => ({ value: o.value, label: o.label }))}
        onChange={onChange}
      />
    )
  }

  if (field.control === 'radio') {
    return (
      <div role="radiogroup" aria-label={field.label} className="stack" style={{ gap: 0 }}>
        {(field.options ?? []).map((o) => (
          <label key={o.value} className="row" style={{ minHeight: 44, cursor: disabled ? 'default' : 'pointer' }}>
            <input
              type="radio"
              name={field.key}
              value={o.value}
              checked={asText(value) === o.value}
              disabled={disabled}
              onChange={() => onChange(o.value)}
              style={{ width: 18, height: 18 }}
            />
            <span>{o.label}</span>
          </label>
        ))}
      </div>
    )
  }

  if (field.control === 'checkbox') {
    return (
      <label className="row" style={{ minHeight: 44, cursor: disabled ? 'default' : 'pointer' }}>
        <input
          type="checkbox"
          checked={value === true || value === 'true'}
          disabled={disabled}
          onChange={(e) => onChange(e.currentTarget.checked)}
          style={{ width: 18, height: 18 }}
        />
        <span>{field.label}</span>
      </label>
    )
  }

  if (field.control === 'textarea') {
    return (
      <textarea
        className="line-input"
        rows={3}
        style={{ minHeight: 88, padding: 8, resize: 'vertical' }}
        value={asText(value)}
        disabled={disabled}
        aria-label={field.label}
        onChange={(e) => onChange(e.currentTarget.value)}
      />
    )
  }

  const numeric = DECIMAL_CONTROLS.has(field.control)
  return (
    <Input
      type={field.control === 'date' ? 'date' : 'text'}
      inputMode={numeric ? 'decimal' : undefined}
      align={numeric ? 'right' : 'left'}
      value={asText(value)}
      disabled={disabled}
      invalid={invalid}
      onChange={onChange}
    />
  )
}

export interface FormFieldProps extends ControlProps {
  readonly error?: string | undefined
}

/** Label above the control, the word "Required" printed, hint underneath. */
export const FormField = ({ error, ...props }: FormFieldProps) => {
  const { field } = props
  const full = field.width === 'full' || field.control === 'textarea'
  return (
    <div className={full ? 'field--full' : undefined}>
      <Field label={field.label} required={field.required === true} hint={field.hint} error={error}>
        <Control {...props} invalid={error !== undefined} />
      </Field>
    </div>
  )
}

/**
 * A lookup, with `Add new …` beside it for the masters that are discovered
 * while a document is being raised.
 *
 * What is created is selected straight away — the point is not to have added a
 * record, it is to have filled in this field.
 */
const LookupControl = ({ field, value, onChange, disabled, invalid, valueLabel }: ControlProps) => {
  const entity = field.lookup?.entity ?? ''
  const creatable = CREATABLE[entity]
  const [adding, setAdding] = useState(false)
  const [label, setLabel] = useState<string | undefined>(undefined)

  return (
    <>
      <Lookup
        entity={entity}
        value={asText(value)}
        label={label ?? valueLabel}
        disabled={disabled}
        invalid={invalid}
        onChange={(next, option) => {
          setLabel(option?.label)
          onChange(next)
        }}
      />
      {creatable && !disabled ? (
        <div style={{ marginTop: 6 }}>
          <Button kind="ghost" onClick={() => setAdding(true)}>
            Add new {field.label.toLowerCase()}
          </Button>
        </div>
      ) : null}

      {adding && creatable ? (
        <QuickMasterDialog
          masterKey={creatable}
          title={`New ${field.label.toLowerCase()}`}
          onClose={() => setAdding(false)}
          onCreated={(id, created) => {
            setLabel(created)
            onChange(id)
            setAdding(false)
          }}
        />
      ) : null}
    </>
  )
}
